/**
 * Page-level SEO titles and descriptions for static and known routes.
 * Layout uses this to set SEOMeta when the current path matches.
 * Pages that set their own Helmet (e.g. FreeToolsPage, LandingPage) will override.
 */

export interface RouteSEOMeta {
  title: string;
  description?: string;
}

/** Path or path prefix -> meta. For dynamic routes use prefix (e.g. /blog) and the page can override. */
export const ROUTE_SEO: Record<string, RouteSEOMeta> = {
  '/': {
    title: 'Focuzon | Deep Work, Pomodoro Timer & Task Management Tool',
    description:
      'Focuzon helps you stay focused using Pomodoro timers, deep work sessions and productivity tools.',
  },
  '/login': {
    title: 'Login | Focuzon',
    description: 'Log in to Focuzon to access your focus sessions, tasks and productivity tools.',
  },
  '/signup': {
    title: 'Sign Up | Focuzon',
    description: 'Create your Focuzon account and start focusing with Pomodoro and deep work timers.',
  },
  '/forgot-password': {
    title: 'Forgot Password | Focuzon',
    description: 'Reset your Focuzon account password.',
  },
  '/reset-password': {
    title: 'Reset Password | Focuzon',
    description: 'Set a new password for your Focuzon account.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Focuzon',
    description: 'Focuzon privacy policy and how we handle your data.',
  },
  '/about': {
    title: 'About Us | Focuzon',
    description: 'Learn about Focuzon and our mission to help you focus and get more done.',
  },
  '/blog': {
    title: 'Blog | Focuzon',
    description: 'Productivity tips, deep work and focus articles from Focuzon.',
  },
  '/gdpr': {
    title: 'GDPR Compliance | Focuzon',
    description: 'Focuzon GDPR compliance and data protection information.',
  },
  '/tools': {
    title: 'Free Productivity Tools | Password Generator, Currency Converter & More | Focuzon',
    description:
      'Free productivity tools: password generator, currency converter, word counter, unit converter, QR code. No login required.',
  },
  // Main product pages (use when routes are added)
  '/pomodoro-timer': {
    title: 'Pomodoro Timer Online | Focuzon',
    description: 'Free Pomodoro timer for focused work sessions and breaks. Start a focus session with Focuzon.',
  },
  '/deep-work': {
    title: 'Deep Work for Focused Work | Focuzon',
    description: 'Deep work timer to block distractions and focus for longer. Part of Focuzon productivity platform.',
  },
  '/focus-sounds': {
    title: 'Focus Sounds for Productivity | Focuzon',
    description: 'Focus timer to track and protect your deep work time. Stay productive with Focuzon.',
  },
  '/task-manager': {
    title: 'Task Management Tool for Focused Work | Focuzon',
    description: 'Task manager built for focused work. Organize tasks and get more done with Focuzon.',
  },
  '/productivity-tools': {
    title: 'Best Productivity Tools for Deep Work | Focuzon',
    description: 'Free productivity tools: timers, invoice generator, calculators and more.',
  },
  '/distraction-blocker': {
    title: 'Distraction Blocker | Focuzon',
    description: 'Block distractions and stay in flow with Focuzon distraction blocker.',
  },
  '/work-session-timer': {
    title: 'Work Session Timer | Focuzon',
    description: 'Track work sessions and focus time with the Focuzon work session timer.',
  },
  '/focus-timer': {
    title: 'Focus Timer | Focuzon',
    description: 'Focus timer for productivity. Redirects to work session timer.',
  },
  '/dashboard': {
    title: 'Dashboard | Focuzon',
    description: 'Your Focuzon dashboard. Start a focus session, manage tasks and view progress.',
  },
  '/invoice': {
    title: 'Invoices | Focuzon',
    description: 'Create and manage invoices. Focuzon for freelancers and small teams.',
  },
};

/**
 * Get SEO meta for a path. Checks exact match first, then prefix (e.g. /tools/password-generator -> /tools).
 * Returns undefined if no match (layout will use default SEOMeta).
 */
export function getRouteSEO(pathname: string): RouteSEOMeta | undefined {
  const exact = ROUTE_SEO[pathname];
  if (exact) return exact;
  // Prefix match: e.g. /tools/anything -> use /tools
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length; i >= 1; i--) {
    const prefix = '/' + segments.slice(0, i).join('/');
    if (ROUTE_SEO[prefix]) return ROUTE_SEO[prefix];
  }
  return undefined;
}
